/**
 * Stripe Client Module
 * 
 * Client-side utilities for Stripe integration.
 * Handles checkout session creation and customer portal redirects.
 */

import { supabase } from '@/lib/supabase/client';

// Stripe publishable key from environment
const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;

// Lazy-load Stripe.js
let stripePromise: Promise<import('@stripe/stripe-js').Stripe | null> | null = null;

/**
 * Get the Stripe instance (lazy-loaded)
 */
export async function getStripe() {
  if (!STRIPE_PUBLISHABLE_KEY) {
    console.warn('Stripe publishable key not configured');
    return null;
  }

  if (!stripePromise) {
    const { loadStripe } = await import('@stripe/stripe-js');
    stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
  }

  return stripePromise;
}

/**
 * Create a checkout session and redirect to Stripe Checkout
 */
export async function redirectToCheckout(
  childId: string,
  options?: {
    priceId?: string;
    successUrl?: string;
    cancelUrl?: string;
  }
): Promise<{ error?: string }> {
  try {
    // Get current session token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return { error: 'Not authenticated' };
    }

    // Call edge function to create checkout session
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-create-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          childId,
          priceId: options?.priceId,
          successUrl: options?.successUrl || `${window.location.origin}/settings?checkout=success`,
          cancelUrl: options?.cancelUrl || `${window.location.origin}/settings?checkout=canceled`,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      return { error: data.error || 'Failed to create checkout session' };
    }

    if (!data.checkoutUrl) {
      return { error: 'No checkout URL returned' };
    }

    // Redirect to Stripe Checkout
    window.location.href = data.checkoutUrl;
    return {};
  } catch (error) {
    console.error('Checkout error:', error);
    return { error: error instanceof Error ? error.message : 'Checkout failed' };
  }
}

/**
 * Open Stripe Customer Portal for billing management
 */
export async function openCustomerPortal(
  options?: {
    returnUrl?: string;
  }
): Promise<{ error?: string }> {
  try {
    // Get current session token
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      return { error: 'Not authenticated' };
    }

    // Call edge function to create portal session
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/stripe-customer-portal`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          returnUrl: options?.returnUrl || `${window.location.origin}/settings`,
        }),
      }
    );

    const data = await response.json();

    if (!response.ok) {
      // Special handling for no customer
      if (data.code === 'NO_CUSTOMER') {
        return { error: 'No billing account found. Subscribe to a plan first.' };
      }
      return { error: data.error || 'Failed to open billing portal' };
    }

    if (!data.portalUrl) {
      return { error: 'No portal URL returned' };
    }

    // Redirect to Stripe Customer Portal
    window.location.href = data.portalUrl;
    return {};
  } catch (error) {
    console.error('Customer portal error:', error);
    return { error: error instanceof Error ? error.message : 'Portal access failed' };
  }
}

/**
 * Check if Stripe is configured
 */
export function isStripeConfigured(): boolean {
  return !!STRIPE_PUBLISHABLE_KEY;
}
